import type { ReactElement } from 'react';

import { LegalDoc } from '@/widgets/legal-doc';
import { COMPANY } from '@/shared/config';

const EFFECTIVE = '2026년 9월 1일';

/**
 * 광고성 정보 수신 동의 — 14. 1차 오픈 범위 §5.3.
 *
 * 가입 화면과 푸터의 「보기」가 닿는 곳이다. 이 페이지가 없으면 고지가 성립하지 않는다.
 *
 * **매체를 이메일로 특정해 적는다.** `consents.type` 이 `MARKETING_EMAIL` 인데 문구가
 * 포괄적이면 어느 쪽이 진짜 동의인지 다퉈진다. 카카오·문자를 붙일 때는 이 문서가 아니라
 * 매체별 동의를 새로 받아야 한다.
 *
 * 여기서 약속한 「로그인 없는 수신거부」는 `/unsubscribe` 가 받는다. Gmail SMTP 는 수신거부
 * 링크를 자동으로 붙여주지 않으므로, **발송 쪽에서 토큰을 만들어 링크를 실어 보내야** 이 문장이
 * 사실이 된다. 화면만 있고 링크가 안 나가면 약속을 못 지킨 것이다.
 */
export function MarketingConsentPage(): ReactElement {
  return (
    <LegalDoc
      title="광고성 정보 수신 동의"
      effectiveDate={EFFECTIVE}
      articles={[
        {
          id: 'purpose',
          title: '수집·이용 목적',
          body: (
            <>
              <p>
                {COMPANY.legalName}는 동의하신 분께 아래 내용의 광고성 정보를 보내기 위해 개인정보를
                이용합니다.
              </p>
              <ul className="flex list-disc flex-col gap-1 pl-5">
                <li>새 자료 출시 안내</li>
                <li>교육 프로그램 안내</li>
                <li>할인·이벤트 안내</li>
              </ul>
            </>
          ),
        },
        {
          id: 'items',
          title: '수집·이용 항목',
          body: <p>이메일 주소</p>,
        },
        {
          id: 'channel',
          title: '전송 매체',
          body: (
            <>
              <p>
                <strong>이메일</strong>로만 보냅니다.
              </p>
              <p>
                문자·카카오톡 등 다른 매체로 보내려면 해당 매체에 대한 동의를 별도로 받습니다. 이
                동의만으로는 다른 매체로 보내지 않습니다.
              </p>
            </>
          ),
        },
        {
          id: 'labeling',
          title: '광고성 정보의 표기',
          body: (
            <>
              <p>광고성 메일에는 다음을 표기합니다.</p>
              <ul className="flex list-disc flex-col gap-1 pl-5">
                <li>
                  제목 앞에 <strong>(광고)</strong> 표기
                </li>
                <li>전송자의 명칭과 연락처</li>
                <li>수신거부 방법과 링크</li>
              </ul>
              <p>
                자료 전달, 로그인 링크, 문의 답변 등 <strong>이용자가 요청한 정보성 메일</strong>은
                이 동의와 무관하게 발송되며, 광고성 내용을 함께 담지 않습니다.
              </p>
            </>
          ),
        },
        {
          id: 'retention',
          title: '보유·이용 기간',
          body: (
            <>
              <p>수신 동의를 철회하시거나 회원에서 탈퇴하실 때까지 보유·이용합니다.</p>
              <p>
                수신 동의 후 일정 기간이 지나면 동의를 유지하실 것인지 다시 여쭙고, 응답이 없으면
                발송을 중단합니다.
              </p>
              <p>
                철회하신 경우에도 처리 사실을 확인하기 위해 동의·철회 기록을 철회 후{' '}
                <strong>6개월간</strong> 보관합니다.
              </p>
            </>
          ),
        },
        {
          id: 'right-to-refuse',
          title: '동의를 거부할 권리와 불이익',
          body: (
            <>
              <p>이 동의는 선택 사항입니다. 동의를 거부하실 수 있습니다.</p>
              <p>
                <strong>
                  동의하지 않으셔도 회원 가입과 자료 다운로드를 포함한 서비스 이용에 아무런 제한이
                  없습니다.
                </strong>
              </p>
            </>
          ),
        },
        {
          id: 'withdraw',
          title: '수신 동의 철회 방법',
          body: (
            <>
              <p>다음 두 가지 방법으로 언제든지 철회하실 수 있습니다.</p>
              <ul className="flex list-disc flex-col gap-1 pl-5">
                <li>
                  보내드린 메일 하단의 수신거부 링크를 누르는 방법 —{' '}
                  <strong>로그인이 필요 없습니다</strong>
                </li>
                <li>로그인 후 마이페이지 &gt; 수신 설정에서 끄는 방법</li>
              </ul>
              <p>철회하시면 그 시점부터 광고성 정보를 보내지 않습니다.</p>
              <p>문의 — {COMPANY.email}</p>
            </>
          ),
        },
      ]}
    />
  );
}
